import DragIndicatorIcon from "@mui/icons-material/DragIndicatorRounded";
import { Button, Divider, List, ListItem, ListItemIcon, Stack, Typography } from "@mui/material";
import deepEqual from "fast-deep-equal";
import { useMemo, useState } from "react";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { numberComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import DragAndDropList from "../../../common/DragAndDropList";
import GlobalDndProvider from "../../../common/GlobalDndProvider";
import ApprovalConfigurationContext from "./ApprovalConfigurationContext";

interface Props {
  onClose: () => void;
}

const ApprovalStepsOrderEditor = ({ onClose }: Props) => {
  const { approvalConfiguration, saveConfiguration } = useDefinedContext(ApprovalConfigurationContext);

  const [stepNames, setStepNames] = useState<string[]>(approvalConfiguration.steps.map((step) => step.name));
  const [saving, setSaving] = useState(false);

  const initialStepNames = useMemo(
    () => approvalConfiguration.steps.map((step) => step.name),
    [approvalConfiguration.steps]
  );

  const isDirty = useMemo(() => !deepEqual(stepNames, initialStepNames), [initialStepNames, stepNames]);

  const handleApply = async () => {
    const newSteps = [...approvalConfiguration.steps].sort(numberComparerBy((step) => stepNames.indexOf(step.name)));

    setSaving(true);
    const saved = await saveConfiguration(
      {
        steps: newSteps,
        conversionTargetCurrencyCode: approvalConfiguration.conversionTargetCurrencyCode,
        approversAssignmentMode: approvalConfiguration.approversAssignmentMode,
      },
      "Step order updated successfully"
    );
    setSaving(false);

    if (saved) {
      onClose();
    }
  };

  const handleCancel = () => {
    onClose();
    setTimeout(() => setStepNames(initialStepNames), 100);
  };

  const handleItemDrop = (dropIndex: number, draggedItem: string | undefined) => {
    if (!draggedItem) {
      return;
    }

    setStepNames((prev) => {
      const newStepNames = prev.filter((col) => col !== draggedItem);
      const insertIndex = dropIndex < 0 ? newStepNames.length : dropIndex;
      newStepNames.splice(insertIndex, 0, draggedItem);
      return newStepNames;
    });
  };

  return (
    <Stack sx={{ overflow: "hidden", alignItems: "flex-start", width: "26.25rem", maxHeight: "80vh" }}>
      <Stack
        direction="row"
        sx={{ px: 2.5, py: 2, justifyContent: "space-between", alignItems: "center", width: "100%" }}
      >
        <Typography variant="h6">Step Order</Typography>
        <CloseIconButton onClick={handleCancel} />
      </Stack>
      <Divider flexItem />
      <List sx={{ width: "100%", px: 0.5, py: 1, overflow: "auto" }}>
        <GlobalDndProvider>
          <DragAndDropList
            type="approval_step_name"
            items={stepNames}
            getItemKey={(stepName) => stepName}
            onDrop={handleItemDrop}
            renderItem={(stepName) => (
              <ListItem draggable>
                <ListItemIcon sx={{ minWidth: "unset", mr: 0.75 }}>
                  <DragIndicatorIcon sx={{ cursor: "grab" }} />
                </ListItemIcon>
                <TypographyTooltipEllipsis text={stepName} />
              </ListItem>
            )}
          />
        </GlobalDndProvider>
      </List>
      <Divider flexItem />
      <Stack spacing={1} direction="row" sx={{ px: 2, py: 1.5, justifyContent: "flex-end", width: "100%" }}>
        <Button variant="text" color="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" loading={saving} disabled={!isDirty} onClick={handleApply}>
          Apply
        </Button>
      </Stack>
    </Stack>
  );
};

export default ApprovalStepsOrderEditor;
